/**
 * What the session is being recorded for. It decides which of the live
 * screen's panels open when the meeting starts, and — since BRAI-460 — which
 * `meetingMode` the STT socket is opened with.
 *
 * The form labels this "Mode", but the code deliberately says `kind`: `mode`
 * on the same object is the translation direction (`one_way` / `two_way`),
 * whose cards sit right below these ones, and two fields called mode is how
 * the wrong one gets read (BRAI-416).
 *
 * `meeting`, `interview` and `presentation` behave identically on the wire
 * today — the server only separates them in its own logs. `internal_meeting`
 * is a different protocol altogether: nothing is transcribed or translated
 * while the meeting runs, and the minutes arrive afterwards over HTTP.
 */
export type SessionKind = 'meeting' | 'interview' | 'presentation' | 'internal_meeting'

/** Selector order, which is also the order the modal lists them in. */
export const SESSION_KINDS: readonly SessionKind[] = [
  'meeting',
  'interview',
  'presentation',
  'internal_meeting',
]

/**
 * The one kind whose socket speaks a different protocol.
 *
 * A predicate rather than a comparison at each call site: every branch that
 * turns realtime behaviour off has to agree on the answer, and a stray
 * `=== 'internal_meeting'` somewhere is how one of them ends up disagreeing.
 */
export function isOfflineSessionKind(kind: SessionKind): boolean {
  return kind === 'internal_meeting'
}

export const DEFAULT_SESSION_KIND: SessionKind = 'meeting'

/**
 * The Modes the create form offers, which is deliberately a shorter list than
 * {@link SESSION_KINDS} rather than the same one.
 *
 * {@link SESSION_KINDS} is the allow-list {@link sanitizeSessionKind} checks
 * every persisted and server-sent value against, and `resolveRouteSessionKind`
 * runs that guard over meetings that already exist. Dropping a kind from it
 * would re-read a meeting already recorded as `presentation` as a plain
 * meeting and quietly change the panels its live screen opens — a form that
 * stops offering a Mode should not rewrite the history behind it.
 *
 * Presentation Mode is not part of release v2.1 (BRAI-586), so it is missing
 * here and present there.
 */
export const SELECTABLE_SESSION_KINDS: readonly SessionKind[] = [
  'meeting',
  'interview',
  'internal_meeting',
]

/** Which of the live screen's five collapsible panels start open. */
export interface SessionKindPanelLayout {
  /** Input — the source-language transcript. */
  sourceOpen: boolean
  /** Output — the translated transcript. */
  targetOpen: boolean
  /** Enhanced Mode — the batched, cleaned-up rendering. */
  batchOpen: boolean
  /** Summary — the rolling live memo. */
  memoOpen: boolean
  keyPointsOpen: boolean
}

/**
 * Nothing here is locked — every panel stays collapsible on the live screen,
 * so the mode only picks the starting point.
 *
 * `meeting` and `interview` deliberately share one opening row. The live screen
 * starts on Input and Output alone; Enhanced, Summary and Key Points stay one
 * press away. The Modes still differ in {@link SESSION_KIND_EMPHASIS}, which
 * is about how loudly a new line lands rather than which panes are up.
 */
export const SESSION_KIND_PANEL_LAYOUT: Record<SessionKind, SessionKindPanelLayout> = {
  // Transcript only. Summary and Key Points are the panes a reader opens when
  // they want the analysis, and Enhanced restates turns the transcript already
  // shows — so none of the three is on the screen until asked for.
  meeting: {
    sourceOpen: true,
    targetOpen: true,
    batchOpen: false,
    memoOpen: false,
    keyPointsOpen: false,
  },
  interview: {
    sourceOpen: true,
    targetOpen: true,
    batchOpen: false,
    memoOpen: false,
    keyPointsOpen: false,
  },
  // Left on the analysis-led row the other two opened on until this change.
  // Presentation is not selectable in v2.1 (BRAI-586), so nothing creates a
  // meeting of this kind today and the only rows that reach it are ones already
  // recorded as `presentation` — which is exactly the history
  // SELECTABLE_SESSION_KINDS' note says not to rewrite.
  presentation: {
    sourceOpen: false,
    targetOpen: false,
    batchOpen: true,
    memoOpen: true,
    keyPointsOpen: true,
  },
  // Nothing is streamed during an internal meeting, so every panel would open
  // onto an empty pane that never fills. The live screen shows a recording
  // state instead — see `isOfflineSessionKind`.
  internal_meeting: {
    sourceOpen: false,
    targetOpen: false,
    batchOpen: false,
    memoOpen: false,
    keyPointsOpen: false,
  },
}

/**
 * The opening layout for a kind, with the Teams side panel's override applied.
 *
 * Docked beside a call the panel is a narrow column, and the transcript is the
 * thing somebody opened it to watch — so there it starts on Input and Output
 * alone, whatever the Mode says, with Enhanced, Summary and Key Points folded
 * away.
 *
 * BRAI-572 opened those two without closing the rest, which left Meeting mode
 * showing all five at once in a column too narrow for one. The reasoning then
 * was that the other three still belong to the Mode and the panel should not
 * redefine what a Mode means. What that missed is the width: on this screen a
 * Mode's layout is a claim about how to spend the space, and the panel has a
 * different amount of it. Every pane is still one press from opening, so what
 * the Mode picks is a starting point here too — just a different one.
 *
 * An override rather than a sixth entry in {@link SESSION_KIND_PANEL_LAYOUT}:
 * the panel is a place the same kinds are run from, not a kind of its own.
 *
 * Offline kinds are left alone. Nothing is streamed during an internal meeting,
 * so opening those two panes puts up a pair that never fills — which is exactly
 * why that row closes them in the first place.
 *
 * @param kind The Mode picked in the create form.
 * @param options `inTeamsPanel` when the live screen is rendering inside the
 *   Microsoft Teams side panel.
 * @returns Which panels start open. Never locked — each one stays collapsible.
 */
export function sessionKindPanelLayout(
  kind: SessionKind,
  options: { inTeamsPanel?: boolean } = {},
): SessionKindPanelLayout {
  const layout = SESSION_KIND_PANEL_LAYOUT[kind]
  if (!options.inTeamsPanel || isOfflineSessionKind(kind)) return layout
  return {
    sourceOpen: true,
    targetOpen: true,
    batchOpen: false,
    memoOpen: false,
    keyPointsOpen: false,
  }
}

/** How loudly the live panes mark whatever arrived most recently. */
export interface SessionKindEmphasis {
  /**
   * Output tints its newest utterance and bumps that text a couple of pixels.
   *
   * One flag for both effects because both ride on the same pair of classes:
   * `.output-highlight-on` carries the colour and
   * `.output-highlight.output-highlight-on` the size (`index.css`).
   */
  outputHighlight: boolean
  /**
   * Enhanced Mode enlarges the newest turn's translated line.
   *
   * Its tint is a separate matter and stays on either way — Enhanced is a lead
   * panel in Meeting mode, so knowing which turn is new is still worth having.
   * What goes is the text changing size as turns arrive.
   */
  enhancedTextBump: boolean
}

/**
 * Off across the board for `meeting`: this emphasis exists to help someone
 * follow speech as it lands, which is the interview job. A meeting reads the
 * transcript beside the analysis panels as reference material, so text that
 * keeps resizing is only movement in the corner of the eye.
 */
export const SESSION_KIND_EMPHASIS: Record<SessionKind, SessionKindEmphasis> = {
  meeting: { outputHighlight: false, enhancedTextBump: false },
  interview: { outputHighlight: true, enhancedTextBump: true },
  presentation: { outputHighlight: false, enhancedTextBump: false },
  // Moot: an internal meeting renders no live panes to emphasise.
  internal_meeting: { outputHighlight: false, enhancedTextBump: false },
}

/**
 * Guard a mode coming back from persisted store state, which lives in
 * localStorage and therefore survives version changes and hand editing.
 *
 * @param value Untrusted candidate, typically `settings.meetingSetup.sessionKind`.
 * @returns `value` when it names a built mode, otherwise {@link DEFAULT_SESSION_KIND}.
 */
export function sanitizeSessionKind(
  value: unknown,
  fallback: SessionKind = DEFAULT_SESSION_KIND,
): SessionKind {
  // Checked against the list rather than by comparing to each name: this guard
  // was already one edit behind the type once, and a kind the form can pick but
  // this rejects is silently downgraded on the next reload.
  return SESSION_KINDS.includes(value as SessionKind) ? (value as SessionKind) : fallback
}
